// IEEE 830 / ISO/IEC/IEEE 29148 compliant requirements engine (offline)

export type ReqType = "functional" | "nonfunctional";
export type Priority = "Must" | "Should" | "Could";

export interface Requirement {
  id: string;
  type: ReqType;
  text: string;
  priority: Priority;
  verification: "Test" | "Inspection" | "Demonstration" | "Analysis";
}

export interface FeaturePattern {
  requirements: Requirement[];
  acceptance_criteria: string[];
  edge_cases: string[];
}

export interface Ambiguity {
  word: string;
  severity: "high" | "medium" | "low";
  suggestion: string;
}

export interface AnalysisResult {
  detected_features: string[];
  requirements: Requirement[];
  acceptance_criteria: string[];
  edge_cases: string[];
  ambiguities: Ambiguity[];
  score: number;
  source: string;
}

const r = (
  id: string,
  type: ReqType,
  text: string,
  priority: Priority = "Must",
  verification: Requirement["verification"] = "Test",
): Requirement => ({ id, type, text, priority, verification });

export const FEATURE_PATTERNS: Record<string, FeaturePattern> = {
  login: {
    requirements: [
      r("REQ-AUTH-001", "functional", "Upon valid credentials entry, the system shall authenticate the user within 2 seconds."),
      r("REQ-AUTH-002", "functional", "Upon invalid credentials entry, the system shall display a generic error message without revealing which field is incorrect."),
      r("REQ-AUTH-003", "functional", "Upon 3 consecutive failed login attempts, the system shall lock the account for 30 minutes."),
      r("REQ-AUTH-004", "functional", "The system shall provide a 'Forgot Password' flow that delivers a single-use reset link valid for 15 minutes.", "Should"),
      r("NF-SEC-AUTH-001", "nonfunctional", "Passwords shall be hashed using bcrypt with a minimum cost factor of 12.", "Must", "Inspection"),
      r("NF-SEC-AUTH-002", "nonfunctional", "All authentication traffic shall be encrypted using TLS 1.2 or higher.", "Must", "Inspection"),
    ],
    acceptance_criteria: [
      "Given a registered user with valid credentials, When they submit the login form, Then they are redirected to the dashboard within 2 seconds.",
      "Given a registered user with invalid password, When they submit the login form, Then they see 'Invalid credentials' and the specific failing field is not identified.",
      "Given a user who has failed login 3 times, When they attempt a 4th login, Then the account is locked and a notification email is sent.",
    ],
    edge_cases: [
      "User attempts login with expired password — system shall force password reset",
      "User attempts login during maintenance window — system shall display maintenance message",
      "User account is disabled by admin — system shall display 'account suspended' message",
    ],
  },
  payment: {
    requirements: [
      r("REQ-PAY-001", "functional", "Upon checkout, the system shall calculate total including tax and shipping before payment submission."),
      r("REQ-PAY-002", "functional", "Upon payment failure, the system shall preserve cart contents and allow retry without re-entry."),
      r("REQ-PAY-003", "functional", "The system shall send an email receipt within 60 seconds of successful payment."),
      r("NF-PERF-PAY-001", "nonfunctional", "Payment processing shall complete within 5 seconds under normal load (100 concurrent users).", "Must", "Test"),
      r("NF-SEC-PAY-001", "nonfunctional", "The system shall be PCI-DSS Level 1 compliant and shall not store full PAN data.", "Must", "Inspection"),
    ],
    acceptance_criteria: [
      "Given a cart with items totaling $92, When user proceeds to checkout, Then the system displays breakdown: Subtotal $92, Tax $8, Shipping $0, Total $100.",
      "Given a failed payment attempt, When user clicks 'Retry', Then cart contents are preserved and the payment form is pre-filled.",
      "Given a successful payment, When the transaction completes, Then an email receipt is delivered within 60 seconds.",
    ],
    edge_cases: [
      "Payment gateway timeout — system shall queue the transaction and notify user of delayed processing",
      "Duplicate payment submission — system shall detect idempotency key and prevent double-charging",
      "Currency conversion failure — system shall display an error and allow currency selection",
    ],
  },
  search: {
    requirements: [
      r("REQ-SRC-001", "functional", "Upon search query submission, the system shall return results ranked by relevance within 1 second."),
      r("REQ-SRC-002", "functional", "Upon zero results, the system shall suggest alternative queries and display related categories."),
      r("REQ-SRC-003", "functional", "The system shall support filtering by at least category, date, and price range.", "Should"),
    ],
    acceptance_criteria: [
      "Given a search for 'wireless headphones', When user submits the query, Then results display within 1 second sorted by relevance score.",
      "Given a search for 'xyz123nonexistent', When user submits the query, Then the system displays 'No results found' and suggests 'Did you mean: …'.",
    ],
    edge_cases: [
      "Special characters in search query — system shall sanitize input and prevent injection",
      "Search during index rebuild — system shall serve from cached index with stale-data warning",
    ],
  },
  upload: {
    requirements: [
      r("REQ-UPL-001", "functional", "Upon file selection, the system shall validate file type and size before transfer begins."),
      r("REQ-UPL-002", "functional", "The system shall display upload progress as a percentage updated at least once per second."),
      r("REQ-UPL-003", "functional", "Upon transfer failure, the system shall support resumable uploads from the last received byte.", "Should"),
      r("NF-SEC-UPL-001", "nonfunctional", "Uploaded files shall be scanned for malware before being made accessible.", "Must", "Inspection"),
    ],
    acceptance_criteria: [
      "Given a user selects a 10MB PDF, When they confirm upload, Then progress is shown and a success message appears on completion.",
      "Given a user selects a 200MB file when the limit is 100MB, When they confirm upload, Then an explicit size-limit error is displayed.",
    ],
    edge_cases: [
      "Network drops mid-upload — system shall resume from last chunk",
      "User uploads a disallowed MIME type — system shall reject with explicit reason",
      "Storage quota exceeded — system shall block upload and prompt cleanup",
    ],
  },
  notification: {
    requirements: [
      r("REQ-NOT-001", "functional", "Upon a subscribed event, the system shall deliver notifications to all opted-in channels within 30 seconds."),
      r("REQ-NOT-002", "functional", "The system shall allow users to manage channel preferences (email, push, in-app) per notification type."),
      r("NF-AVL-NOT-001", "nonfunctional", "Notification delivery shall achieve 99.5% success rate measured over a rolling 30-day window.", "Must", "Analysis"),
    ],
    acceptance_criteria: [
      "Given a user opted into email and push, When a relevant event fires, Then both channels receive the notification within 30 seconds.",
      "Given a user disables push notifications, When the event fires, Then only email is delivered.",
    ],
    edge_cases: [
      "Delivery provider outage — system shall retry with exponential backoff up to 24 hours",
      "User bounces email repeatedly — system shall suspend email channel and notify user in-app",
    ],
  },
  report: {
    requirements: [
      r("REQ-REP-001", "functional", "The system shall generate reports in PDF, CSV, and XLSX formats."),
      r("REQ-REP-002", "functional", "Upon report request, the system shall queue generation and notify the user when complete."),
      r("NF-PERF-REP-001", "nonfunctional", "Reports up to 100,000 rows shall be generated within 60 seconds.", "Should", "Test"),
    ],
    acceptance_criteria: [
      "Given a user requests a monthly sales report, When generation completes, Then a download link is delivered via in-app notification.",
    ],
    edge_cases: [
      "Empty dataset — system shall produce a valid report with header and 'no data' indicator",
      "Report generation timeout — system shall fall back to chunked export",
    ],
  },
  dashboard: {
    requirements: [
      r("REQ-DSH-001", "functional", "The system shall display key metrics on the dashboard refreshed at least every 60 seconds."),
      r("REQ-DSH-002", "functional", "Users shall be able to customize visible widgets and persist their layout."),
      r("NF-PERF-DSH-001", "nonfunctional", "Initial dashboard render shall complete within 2 seconds on a 4G connection.", "Should", "Test"),
    ],
    acceptance_criteria: [
      "Given a logged-in user, When they open the dashboard, Then widgets render within 2 seconds with live data.",
    ],
    edge_cases: [
      "Backend metrics service unavailable — widgets shall show last-known value with stale indicator",
    ],
  },
  chat: {
    requirements: [
      r("REQ-CHT-001", "functional", "The system shall deliver messages between participants with end-to-end latency under 500ms at p95."),
      r("REQ-CHT-002", "functional", "The system shall persist message history and support search across the last 12 months."),
      r("NF-SEC-CHT-001", "nonfunctional", "Messages shall be encrypted at rest using AES-256.", "Must", "Inspection"),
    ],
    acceptance_criteria: [
      "Given two users in a conversation, When one sends a message, Then the other receives it within 500ms.",
    ],
    edge_cases: [
      "Recipient offline — system shall queue messages and deliver on reconnect",
      "Message exceeds size limit — system shall reject with explicit reason",
    ],
  },
  export: {
    requirements: [
      r("REQ-EXP-001", "functional", "The system shall allow users to export their data in JSON and CSV formats."),
      r("NF-SEC-EXP-001", "nonfunctional", "Export downloads shall be authenticated and links shall expire within 24 hours.", "Must", "Inspection"),
    ],
    acceptance_criteria: [
      "Given a user requests data export, When the file is ready, Then a signed link valid for 24 hours is sent via email.",
    ],
    edge_cases: [
      "Export contains sensitive PII — system shall require step-up authentication before download",
    ],
  },
  api: {
    requirements: [
      r("REQ-API-001", "functional", "The system shall expose a versioned REST API with OpenAPI 3.1 documentation."),
      r("NF-SEC-API-001", "nonfunctional", "All API endpoints shall require OAuth 2.0 bearer tokens.", "Must", "Inspection"),
      r("NF-PERF-API-001", "nonfunctional", "API endpoints shall respond within 300ms at p95 under nominal load.", "Should", "Test"),
    ],
    acceptance_criteria: [
      "Given a valid bearer token, When the client calls GET /v1/resources, Then a 200 response is returned within 300ms.",
    ],
    edge_cases: [
      "Rate limit exceeded — system shall return 429 with Retry-After header",
      "Token expired — system shall return 401 with WWW-Authenticate header",
    ],
  },
};

export const AMBIGUOUS_WORDS: Record<string, { severity: "high" | "medium" | "low"; suggestion: string }> = {
  fast: { severity: "high", suggestion: "Define specific response time (e.g., within 2 seconds)" },
  quickly: { severity: "high", suggestion: "Define specific response time (e.g., within 2 seconds)" },
  "user-friendly": { severity: "high", suggestion: "Define measurable usability metric (e.g., 95% of users complete task in under 3 minutes)" },
  easy: { severity: "high", suggestion: "Define training time or error-rate threshold" },
  intuitive: { severity: "high", suggestion: "Define a measurable usability outcome (task success rate, time-on-task)" },
  secure: { severity: "medium", suggestion: "Reference a specific standard (e.g., OWASP Top 10 compliance, AES-256 encryption)" },
  scalable: { severity: "medium", suggestion: "Define load parameters (e.g., support 10,000 concurrent users)" },
  reliable: { severity: "medium", suggestion: "Define an uptime SLA (e.g., 99.9% availability)" },
  robust: { severity: "low", suggestion: "Define error handling and recovery behavior" },
  efficient: { severity: "low", suggestion: "Define resource utilization limits" },
  modern: { severity: "low", suggestion: "Reference a specific framework/standard or visual baseline" },
  seamless: { severity: "medium", suggestion: "Define measurable transition latency or error rate" },
  flexible: { severity: "low", suggestion: "List concrete configuration points or extension hooks" },
};

const VERBS = /\b(allow|enable|support|provide|display|send|process|validate|authenticate|authorize|log|track|report|export|import|search|filter|sort|notify|alert|upload|download|generate|store|sync|share|invite|approve|reject)\b/gi;
const NOUNS = /\b(user|admin|customer|system|data|file|report|notification|payment|order|product|search|filter|message|invoice|account|profile|team|workspace|document)\b/gi;

function generateGenericRequirements(description: string): Requirement[] {
  const reqs: Requirement[] = [];
  let id = 1;
  const actions = [...new Set((description.match(VERBS) || []).map((s) => s.toLowerCase()))];
  const entities = [...new Set((description.match(NOUNS) || []).map((s) => s.toLowerCase()))];
  const primary = entities[0] || "requested operation";

  actions.forEach((action) => {
    reqs.push(
      r(
        `REQ-GEN-${String(id++).padStart(3, "0")}`,
        "functional",
        `The system shall ${action} the ${primary} per the authenticated user's request.`,
      ),
    );
  });

  if (reqs.length === 0) {
    reqs.push(
      r("REQ-GEN-001", "functional", "The system shall implement the described capability as specified by the product owner."),
    );
  }

  reqs.push(
    r(`NF-SEC-${String(id++).padStart(3, "0")}`, "nonfunctional", "The system shall enforce role-based access control for all sensitive operations.", "Must", "Inspection"),
    r(`NF-PERF-${String(id++).padStart(3, "0")}`, "nonfunctional", "The system shall respond to user requests within 3 seconds under normal load.", "Should", "Test"),
    r(`NF-AVL-${String(id++).padStart(3, "0")}`, "nonfunctional", "The system shall maintain 99.5% uptime excluding scheduled maintenance windows.", "Should", "Analysis"),
  );

  return reqs;
}

export function analyzeRequirements(description: string): AnalysisResult {
  const lower = description.toLowerCase();
  const result: AnalysisResult = {
    detected_features: [],
    requirements: [],
    acceptance_criteria: [],
    edge_cases: [],
    ambiguities: [],
    score: 100,
    source: "",
  };

  for (const [feature, data] of Object.entries(FEATURE_PATTERNS)) {
    if (lower.includes(feature)) {
      result.detected_features.push(feature);
      result.requirements.push(...data.requirements);
      result.acceptance_criteria.push(...data.acceptance_criteria);
      result.edge_cases.push(...data.edge_cases);
    }
  }

  for (const [word, data] of Object.entries(AMBIGUOUS_WORDS)) {
    const re = new RegExp(`\\b${word}\\b`, "i");
    if (re.test(description)) {
      result.ambiguities.push({ word, ...data });
      result.score -= data.severity === "high" ? 15 : data.severity === "medium" ? 10 : 5;
    }
  }

  if (result.requirements.length === 0) {
    result.requirements = generateGenericRequirements(description);
    result.source = "Rule-based decomposition (no matching pattern library entry).";
  } else {
    result.source = `Matched industry-standard pattern${result.detected_features.length > 1 ? "s" : ""}: ${result.detected_features.join(", ")}.`;
  }

  result.score = Math.max(0, result.score);
  return result;
}

export function toMarkdown(a: AnalysisResult, title: string): string {
  const lines: string[] = [];
  lines.push(`# ${title || "Requirements Specification"}`);
  lines.push("");
  lines.push(`_Source: ${a.source}_`);
  lines.push(`_Quality score: ${a.score}/100_`);
  lines.push("");
  lines.push("## Requirements");
  a.requirements.forEach((r) => {
    lines.push(`- **${r.id}** [${r.type} · ${r.priority} · ${r.verification}] — ${r.text}`);
  });
  lines.push("");
  lines.push("## Acceptance Criteria");
  a.acceptance_criteria.forEach((ac, i) => lines.push(`${i + 1}. ${ac}`));
  lines.push("");
  lines.push("## Edge Cases");
  a.edge_cases.forEach((e) => lines.push(`- ${e}`));
  lines.push("");
  lines.push("## Traceability Matrix");
  lines.push("| ID | Type | Priority | Verification |");
  lines.push("|---|---|---|---|");
  a.requirements.forEach((r) => lines.push(`| ${r.id} | ${r.type} | ${r.priority} | ${r.verification} |`));
  return lines.join("\n");
}

export function toCsv(a: AnalysisResult): string {
  const rows = [["ID", "Type", "Priority", "Verification", "Statement"]];
  a.requirements.forEach((r) => rows.push([r.id, r.type, r.priority, r.verification, `"${r.text.replace(/"/g, '""')}"`]));
  return rows.map((r) => r.join(",")).join("\n");
}

export const TEMPLATE_KEYS = Object.keys(FEATURE_PATTERNS);